// ABOUTME: The crossing runner's state hook: owns the crossing, the request state and the only call to the action endpoint.
// ABOUTME: Blocking actions wait for the save (bounded retry); Stay actions save in the background without ever blocking.

import { useCallback, useEffect, useReducer, useRef } from 'react';
import api from '../../../services/api';
import type { CrossingActionRequest, CrossingDto, CrossingWithProgress } from '../../../types/api';

export type RunnerStatus = 'idle' | 'saving' | 'error';
export type RunnerErrorKind = 'save_failed' | 'refresh' | 'content_removed';

export interface RunnerError {
  kind: RunnerErrorKind;
  message: string;
}

export const SAVE_FAILED_MESSAGE = "Hmm, that didn't go through. Let's try again.";
export const REFRESH_MESSAGE = 'This part needs a quick refresh.';
export const CONTENT_REMOVED_MESSAGE = "That choice isn't here anymore. Pick another one.";
export const STALE_SCREEN_MESSAGE = "This crossing moved ahead on another screen. Here's where it is now.";
export const CROSSING_CLOSED_MESSAGE = 'This crossing is finished. Head back to the dashboard.';

const ATTEMPT_TIMEOUT_MS = 5000;
const RETRY_DELAYS_MS = [500, 1000, 2000]; // four attempts in all
const STAY_RETRY_DELAYS_MS = [500]; // one silent retry
const MAX_RETRY_AFTER_MS = 10_000;
const CONTENT_CODE_FIELDS = ['feeling', 'storm', 'weather'];

interface HttpFailure {
  response?: { status?: number; data?: { error?: string; fields?: string[]; crossing?: CrossingWithProgress }; headers?: Record<string, string> };
}

type Outcome =
  | { kind: 'ok'; crossing: CrossingWithProgress }
  | { kind: 'refused'; status: number; error?: string; fields: string[]; crossing?: CrossingWithProgress }
  | { kind: 'exhausted' }
  | { kind: 'cancelled' };

interface RunnerState {
  crossing: CrossingWithProgress;
  status: RunnerStatus;
  error: RunnerError | null;
  notice: string | null;
  announcement: string | null;
  pressedActionId: string | null;
}

type RunnerEvent =
  | { type: 'send'; actionId: string }
  | { type: 'adopt'; crossing: CrossingWithProgress; notice?: string; announcement?: string }
  | { type: 'adopt_quietly'; crossing: CrossingWithProgress }
  | { type: 'fail'; error: RunnerError }
  | { type: 'reset' };

function reducer(state: RunnerState, event: RunnerEvent): RunnerState {
  switch (event.type) {
    case 'send':
      return { ...state, status: 'saving', error: null, notice: null, announcement: null, pressedActionId: event.actionId };
    case 'adopt':
      return { ...state, crossing: event.crossing, status: 'idle', error: null, notice: event.notice ?? null, announcement: event.announcement ?? null };
    case 'adopt_quietly':
      return { ...state, crossing: event.crossing };
    case 'fail':
      return { ...state, status: 'error', error: event.error };
    case 'reset':
      return { ...state, status: 'idle', error: null };
  }
}

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

function isRetryable(failure: HttpFailure): boolean {
  const status = failure.response?.status;
  return status === undefined || status === 429 || status >= 500;
}

function retryDelayMs(failure: HttpFailure, scheduled: number): number {
  if (failure.response?.status !== 429) return scheduled;
  const seconds = Number(failure.response.headers?.['retry-after']);
  return Number.isFinite(seconds) && seconds >= 0 ? Math.min(seconds * 1000, MAX_RETRY_AFTER_MS) : scheduled;
}

interface UseCrossingRunnerOptions {
  crossingId: number;
  slug?: string;
  token?: string;
  initialCrossing: CrossingDto;
  onNotFound?: () => void;
  refetchContent?: () => void;
}

export function useCrossingRunner({ crossingId, slug, token, initialCrossing, onNotFound, refetchContent }: UseCrossingRunnerOptions) {
  if (!initialCrossing.progress) {
    throw new Error('useCrossingRunner needs a crossing that carries progress');
  }

  const [state, dispatch] = useReducer(reducer, undefined, () => ({
    crossing: initialCrossing as CrossingWithProgress,
    status: 'idle' as RunnerStatus,
    error: null,
    notice: null,
    announcement: null,
    pressedActionId: null,
  }));

  const crossingRef = useRef(state.crossing);
  crossingRef.current = state.crossing;
  const savingRef = useRef(false);
  // Bumped by cancel() and on unmount: a request loop that sees a newer generation stops and drops its result.
  const generationRef = useRef(0);
  const stayRef = useRef<{ inFlight: boolean; pending: { actionId: string; values?: Record<string, unknown> } | null }>({ inFlight: false, pending: null });

  // The callbacks are read at call time, so a caller passing fresh closures never changes run/back/forward identity.
  const callbacksRef = useRef({ onNotFound, refetchContent });
  useEffect(() => { callbacksRef.current = { onNotFound, refetchContent }; });

  useEffect(() => () => { generationRef.current += 1; }, []);

  const url = slug ? `/api/child/${slug}/crossings/${crossingId}/actions` : `/api/crossings/${crossingId}/actions`;

  const post = useCallback(async (request: CrossingActionRequest, generation: number, delays: number[]): Promise<Outcome> => {
    for (let attempt = 0; ; attempt++) {
      try {
        const response = await api.post<CrossingWithProgress>(url, request, { params: token ? { token } : undefined, timeout: ATTEMPT_TIMEOUT_MS });
        return generation === generationRef.current ? { kind: 'ok', crossing: response.data } : { kind: 'cancelled' };
      } catch (err) {
        if (generation !== generationRef.current) return { kind: 'cancelled' };
        const failure = err as HttpFailure;
        if (!isRetryable(failure)) {
          const data = failure.response?.data;
          return { kind: 'refused', status: failure.response!.status!, error: data?.error, fields: data?.fields ?? [], crossing: data?.crossing };
        }
        if (attempt >= delays.length) return { kind: 'exhausted' };
        await sleep(retryDelayMs(failure, delays[attempt]));
        if (generation !== generationRef.current) return { kind: 'cancelled' };
      }
    }
  }, [url, token]);

  const requestFor = (actionId: string, values?: Record<string, unknown>): CrossingActionRequest => {
    const { progress } = crossingRef.current;
    return { screen: progress.screenId, pathIndex: progress.pathIndex, action: actionId, ...(values ? { values } : {}) } as CrossingActionRequest;
  };

  const perform = useCallback(async (actionId: string, values?: Record<string, unknown>, holdAdopt?: Promise<void>) => {
    if (savingRef.current) return;
    savingRef.current = true;
    const generation = generationRef.current;
    dispatch({ type: 'send', actionId });

    const outcome = await post(requestFor(actionId, values), generation, RETRY_DELAYS_MS);
    if (outcome.kind === 'cancelled') return;

    // A screen can ask for the answer to be adopted only once its own transition has finished; the save itself is not delayed,
    // and a failed save is told at once. The runner stays saving meanwhile, so nothing else can be sent.
    if (outcome.kind === 'ok' && holdAdopt) {
      await holdAdopt.catch(err => console.error('Crossing hold on adopting an answer failed', { crossingId, actionId, err }));
      if (generation !== generationRef.current) return;
    }
    savingRef.current = false;

    if (outcome.kind === 'ok') {
      dispatch({ type: 'adopt', crossing: outcome.crossing });
    } else if (outcome.kind === 'exhausted') {
      dispatch({ type: 'fail', error: { kind: 'save_failed', message: SAVE_FAILED_MESSAGE } });
    } else if (outcome.status === 409 && outcome.crossing) {
      const closed = outcome.error === 'crossing_closed';
      const message = closed ? CROSSING_CLOSED_MESSAGE : STALE_SCREEN_MESSAGE;
      dispatch({ type: 'adopt', crossing: outcome.crossing, announcement: message, notice: closed ? undefined : message });
    } else if (outcome.status === 404) {
      dispatch({ type: 'reset' });
      callbacksRef.current.onNotFound?.();
    } else if (outcome.error === 'invalid_values' && outcome.fields.some(field => CONTENT_CODE_FIELDS.includes(field))) {
      callbacksRef.current.refetchContent?.();
      dispatch({ type: 'fail', error: { kind: 'content_removed', message: CONTENT_REMOVED_MESSAGE } });
    } else {
      console.error('Crossing action refused', { crossingId, actionId, status: outcome.status, error: outcome.error });
      dispatch({ type: 'fail', error: { kind: 'refresh', message: REFRESH_MESSAGE } });
    }
  }, [post, crossingId]);

  const stay = useCallback(async (actionId: string, values?: Record<string, unknown>) => {
    const slot = stayRef.current;
    if (slot.inFlight) {
      slot.pending = { actionId, values };
      return;
    }
    slot.inFlight = true;
    const generation = generationRef.current;
    const sentFrom = crossingRef.current.progress;

    const outcome = await post(requestFor(actionId, values), generation, STAY_RETRY_DELAYS_MS);
    slot.inFlight = false;
    if (outcome.kind === 'cancelled') return;

    if (outcome.kind === 'ok') {
      const current = crossingRef.current.progress;
      // A forward action that landed meanwhile owns the record; a late background save never replaces it.
      if (current.screenId === sentFrom.screenId && current.pathIndex === sentFrom.pathIndex) {
        dispatch({ type: 'adopt_quietly', crossing: outcome.crossing });
      }
    } else {
      const reason = outcome.kind === 'refused' ? `${outcome.status} ${outcome.error ?? ''}` : 'unreachable';
      console.warn('Crossing background save dropped', { crossingId, actionId, reason });
    }

    const next = slot.pending;
    slot.pending = null;
    if (next && crossingRef.current.progress.screenId === sentFrom.screenId) {
      void stay(next.actionId, next.values);
    }
  }, [post, crossingId]);

  const run = useCallback((actionId: string, values?: Record<string, unknown>, options?: { holdAdopt?: Promise<void> }) => {
    const action = crossingRef.current.progress.actions.find(a => a.id === actionId);
    // Only an in-screen Stay (the breathing round's log) is a quiet background save. A Stay the plate draws (4A's Create) blocks
    // like any action, so it shows saving and a failed save is told to the family.
    if (action && 'stay' in action.target && action.slot === 'in_screen') {
      void stay(actionId, values);
    } else {
      void perform(actionId, values, options?.holdAdopt);
    }
  }, [perform, stay]);

  const back = useCallback(() => { void perform('back'); }, [perform]);
  const forward = useCallback(() => { void perform('forward'); }, [perform]);

  // Abandons the retry loop (not a request already sent); the record is wherever the last applied action left it.
  const cancel = useCallback(() => {
    generationRef.current += 1;
    savingRef.current = false;
    stayRef.current = { inFlight: false, pending: null };
    dispatch({ type: 'reset' });
  }, []);

  return {
    crossing: state.crossing,
    progress: state.crossing.progress,
    status: state.status,
    saving: state.status === 'saving',
    error: state.error,
    notice: state.notice,
    announcement: state.announcement,
    pressedActionId: state.pressedActionId,
    run,
    back,
    forward,
    cancel,
  };
}
